import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
  detectLocale,
  getAppCopy,
  getHistoryCopy,
  getTooltipCopy,
  localeToHtmlLang,
  setActiveLocale,
  type AppLocale,
  type AppCopy,
} from '../i18n';
import type { HistoryCopy } from '../i18n/historyCopy';
import type { TooltipCopy } from '../i18n/tooltipCopy';

export type LocaleContextValue = {
  locale: AppLocale;
  setLocale: (locale: AppLocale) => void;
  appCopy: AppCopy;
  tooltipCopy: TooltipCopy;
  historyCopy: HistoryCopy;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<AppLocale>(() => {
    const detected = detectLocale();
    setActiveLocale(detected);
    return detected;
  });

  useLayoutEffect(() => {
    setActiveLocale(locale);
    document.documentElement.lang = localeToHtmlLang(locale);
    window.electron?.setMenuLocale?.(locale);
  }, [locale]);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      setLocale: setLocaleState,
      appCopy: getAppCopy(locale),
      tooltipCopy: getTooltipCopy(locale),
      historyCopy: getHistoryCopy(locale),
    }),
    [locale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error('useLocale must be used within LocaleProvider');
  }
  return ctx;
}
